import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Award, ImageIcon, Film, Volume2, FileText } from "lucide-react";
import UserAvatar from "@/components/site/UserAvatar";

/**
 * Featured Creations wall — shows admin-approved user submissions on the homepage.
 * Auto-hides itself when there are zero approved items, so the Landing page stays
 * clean until the community starts sharing.
 */
export default function FeaturedCreations() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/homepage-featured?limit=12");
        if (mounted) setItems(r.data?.items || []);
      } catch {
        if (mounted) setItems([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section className="border-b border-border" data-testid="featured-creations-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="mb-12 max-w-2xl">
          <div className="overline text-primary mb-4 flex items-center gap-2">
            <Award className="w-4 h-4" /> Featured creations
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">
            Made in Draftwave.<br />
            <span className="italic text-muted-foreground">Chosen by the editors.</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground">
            Every piece here was created inside Draftwave and approved by our editorial team.
            Submit yours from the workspace — if we love it, it lands here.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <FeaturedCard key={it.id} item={it} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ item }) {
  const kind = item?.preview?.kind || item?.kind;
  const KindIcon = { text: FileText, image: ImageIcon, video: Film, audio: Volume2 }[kind] || FileText;

  return (
    <article
      className="border border-border bg-background overflow-hidden hover:border-primary transition group"
      data-testid={`featured-card-${item.id}`}
    >
      <div className="relative aspect-[16/10] bg-muted/30 overflow-hidden">
        {item.preview?.image_url ? (
          <img
            src={item.preview.image_url}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : item.preview?.video_url ? (
          <video
            src={item.preview.video_url}
            className="w-full h-full object-cover"
            playsInline
            muted
            loop
            preload="metadata"
            onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
            onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
          />
        ) : item.preview?.audio_url ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-background gap-3">
            <Volume2 className="w-10 h-10 text-primary" />
            <audio src={item.preview.audio_url} controls className="w-4/5" />
          </div>
        ) : (
          <div className="w-full h-full p-6 flex items-center bg-gradient-to-br from-background to-muted/30 overflow-hidden">
            <p className="text-sm text-foreground line-clamp-6 leading-relaxed">
              {item.preview?.text || item.description}
            </p>
          </div>
        )}
        <div className="absolute top-3 left-3 px-2 py-1 bg-background/85 backdrop-blur border border-border overline text-[10px] flex items-center gap-1">
          <KindIcon className="w-3 h-3" /> {kind}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-serif-display text-xl leading-tight line-clamp-2">{item.title}</h3>
        {item.description && (
          <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{item.description}</p>
        )}
        <div className="mt-4 flex items-center gap-2">
          <UserAvatar user={item.user} size={24} />
          <span className="text-xs text-muted-foreground">
            {item.user?.username ? `@${item.user.username}` : item.user?.name || "Draftwave creator"}
          </span>
        </div>
      </div>
    </article>
  );
}
